import React, { forwardRef, useEffect, useRef, useState, ForwardedRef } from 'react'
import ReactDOM from 'react-dom'

import { LoadingOutlined } from '@ant-design/icons'

import { Button } from 'antd'

import i18n from 'i18n'
import classNames from 'classnames'

import { processEmailEvent } from 'emailThreadViewer/helper'

import chevronUp24 from 'assets/images/chevron-up-gray2-24.svg'
import chevronDown24 from 'assets/images/chevron-down-gray2-24.svg'
import exclamation24 from 'assets/images/exclamation-circle-24.svg'
import toolbarCollapse24SVG from 'assets/images/toolbar-collapse-24.svg'
import toolbarExpand24SVG from 'assets/images/toolbar-expand-24.svg'
// import toolbarX24SVG from 'assets/images/toolbar-x-24.svg'

import important24SVG from 'assets/images/toolbar-gmail-important-24.svg'
import importantMark24SVG from 'assets/images/toolbar-gmail-important-mark-24.svg'

import search24SVG from 'assets/images/icon-search-24.svg'

import refreshSVG from 'assets/images/button-refresh-16.svg'
import thumbUpSVG from 'assets/images/button-thumb-up-16.svg'
import thumbUpFilledSVG from 'assets/images/button-thumb-up-filled-16.svg'
import thumbDownSVG from 'assets/images/button-thumb-down-16.svg'
import thumbDownFilledSVG from 'assets/images/button-thumb-down-filled-16.svg'
import aiPreviewSVG from 'assets/images/next-intelligence-24.svg'

import IFace from '../ViewerIFace'

import uiExt from './uiExt'

import styles from './style.module.scss'
import {
  TextFindableNode,
  TextFinderSearchBar,
  useTextFinderContext,
} from 'emailThreadViewer/TextFinder'
import findTextObserver from 'emailThreadViewer/TextFinder/utils/findTextObserver'
import AiExtraction from './AiExtraction/AiExtraction'

interface MailInfo {
  id: string
  has_ai_extraction?: boolean
  ai_extraction?: any
  ai_extraction_feedback?: boolean | null
  [key: string]: any
}

interface ThreadItem {
  emailId: string
  mailInfo: MailInfo
  reactMgr: {
    addReactObj: (obj: any) => void
    removeReactObj: (obj: any) => void
    updateUI: () => void
  }
  fold?: boolean
}

interface ThreadInfo {
  important?: boolean
  labels?: Array<{
    id: string
    name: string
    text_color?: string
    background_color?: string
  }>
  [key: string]: any
}

interface Owner {
  foldAll: (fold: boolean) => void
  setOptimizedBtn?: (emailId: string, value: boolean) => void
}

interface EmailSubjectProps {
  owner: Owner
  threadItem: ThreadItem
  title?: string
  threadInfo?: ThreadInfo | null
  foldThread?: boolean | null
  aiExtractionInitialOpen?: boolean
  aiExtractionResetToken?: number
  forwardedRef?: ForwardedRef<any>
  registerFindableNode?: any
}

interface EmailSubjectState {
  aiExtraction: boolean
  aiExtractionRefresh: boolean
  aiExtractionLoading: boolean
  taskStatusLoadingMap: Record<string, boolean>
  eventStatusLoadingMap: Record<string, boolean>
  foldThread: boolean | null
  threadImportant: boolean | null
  foldAiExtraction: boolean
  threadImportantLoading?: boolean
}

interface InfoItem {
  key: string
  value?: string
  histories?: Array<{
    date?: string
    value: string
  }>
}

class EmailSubject extends React.Component<EmailSubjectProps, EmailSubjectState> {
  foldMap: Record<string, boolean> = {}
  aiExtractionRequested: boolean = false
  aiSummaryRequested?: boolean

  constructor(props: EmailSubjectProps) {
    super(props)

    this.state = {
      aiExtraction: props.aiExtractionInitialOpen && !!props.threadItem.mailInfo.ai_extraction,
      aiExtractionRefresh: false,
      aiExtractionLoading: false,
      taskStatusLoadingMap: {},
      eventStatusLoadingMap: {},
      foldThread: null,
      threadImportant: null,
      foldAiExtraction: false,
    }

    this.foldMap = {}
    this.aiExtractionRequested = false
  }

  componentDidMount(): void {
    this.initData()
    this.syncFoldThread()
    this.syncThreadImportant()
  }

  componentDidUpdate(prevProps: EmailSubjectProps): void {
    const { threadItem, foldThread, threadInfo } = this.props

    if (prevProps != null) {
      if (prevProps.threadItem !== threadItem) {
        prevProps.threadItem.reactMgr.removeReactObj(this)
        this.initData()
      }
      if (prevProps.foldThread !== foldThread) {
        this.syncFoldThread()
      }

      const pImportant = prevProps.threadInfo != null ? prevProps.threadInfo.important : null
      const important = threadInfo != null ? threadInfo.important : null

      if (pImportant !== important) {
        this.syncThreadImportant()
      }

      if (prevProps.aiExtractionResetToken !== this.props.aiExtractionResetToken) {
        this.resetAiExtractionState()
      }
    }
  }

  resetAiExtractionState = (): void => {
    const { aiExtractionInitialOpen, threadItem } = this.props
    const mailInfo = threadItem?.mailInfo || ({} as MailInfo)
    const hasExtraction = !!mailInfo.ai_extraction

    this.aiExtractionRequested = hasExtraction

    this.setState({
      aiExtraction: !!aiExtractionInitialOpen && hasExtraction,
      aiExtractionRefresh: false,
      aiExtractionLoading: false,
    })
  }

  componentWillUnmount(): void {
    const { threadItem } = this.props
    const { reactMgr } = threadItem

    reactMgr.removeReactObj(this)
  }

  initData = (): void => {
    const { threadItem } = this.props
    const { mailInfo, reactMgr } = threadItem

    const { ai_extraction: aiExtrationData } = mailInfo

    this.aiExtractionRequested = aiExtrationData != null

    reactMgr.addReactObj(this)
  }

  syncFoldThread = (): void => {
    const { foldThread } = this.props

    this.setState({
      foldThread,
    })
  }

  syncThreadImportant = (): void => {
    const { threadInfo } = this.props
    const important = threadInfo != null ? threadInfo.important : null

    this.setState({
      threadImportant: important ?? null,
    })
  }

  onthreadImportantStateChanged = (fold: boolean): void => {
    // const { owner } = this.props
    console.log(' --- onthreadImportantStateChanged : ', fold)

    this.setState({
      threadImportantLoading: true,
    })

    IFace.WebToNative.setThreadImportant(fold).then((res: any) => {
      console.log(' --- setThreadImportant res : ', res)
      const newState: Partial<EmailSubjectState> = {
        threadImportantLoading: false,
      }
      if (res != null) {
        newState.threadImportant = res
      }
      this.setState(newState as EmailSubjectState)
    })
  }

  onfoldThreadStateChanged = (fold: boolean): void => {
    const { owner } = this.props
    console.log(' --- onfoldThreadStateChanged : ', fold)

    owner.foldAll(fold)
  }

  onaiExtractionStateChanged = (fold: boolean): void => {
    console.log(' --- onaiExtractionStateChanged : ', fold)

    const { threadItem } = this.props
    const { mailInfo } = threadItem

    const { has_ai_extraction: hasAiExtration, ai_extraction: aiExtrationData } = mailInfo

    if (hasAiExtration === true && aiExtrationData == null && this.aiExtractionRequested !== true) {
      this.aiExtractionRequested = true

      this.requestAiExtraction(false)
    }
  }

  requestAiExtraction = (refresh: boolean): void => {
    const { threadItem } = this.props
    const { mailInfo, reactMgr } = threadItem

    if (refresh === true) {
      this.setState({
        aiExtractionRefresh: true,
      })
    } else {
      this.setState({
        aiExtractionLoading: true,
      })
    }

    IFace.WebToNative.getAiExtraction(mailInfo.id, refresh).then((ret: any) => {
      const { data, remainLoadingCnt } = ret
      if (data != null && data.success !== false) {
        if (data.has_ai_extraction !== undefined) {
          mailInfo.has_ai_extraction = data.has_ai_extraction
        }
        mailInfo.ai_extraction = data.ai_extraction
        mailInfo.ai_extraction_feedback = data.ai_extraction_feedback

        if (remainLoadingCnt > 0) {
          // this.forceUpdate()
          reactMgr.updateUI()
        } else {
          this.setState({
            aiExtractionLoading: false,
            aiExtractionRefresh: false,
          })
          reactMgr.updateUI()
        }
      } else {
        const failState: Partial<EmailSubjectState> = {
          aiExtractionLoading: false,
          aiExtractionRefresh: false,
        }

        if (refresh !== true) {
          this.aiSummaryRequested = false
          ;(failState as any).aiSummary = false
        }
        this.setState(failState as EmailSubjectState)
      }
    })
  }

  onClickBtn = (cmd: string, _e: React.MouseEvent, ..._params: any[]): void => {
    const { threadItem } = this.props
    const { mailInfo } = threadItem

    // console.log(' --- onClickBtn : ', cmd);

    switch (cmd) {
      case 'extraction-feedback-thumbUp':
        IFace.WebToNative.aiExtractionFeedback(mailInfo.id, 'UP')
        break
      case 'extraction-feedback-thumbDown':
        IFace.WebToNative.aiExtractionFeedback(mailInfo.id, 'DOWN')
        break
      case 'refresh-ai-extraction':
        this.requestAiExtraction(true)
        break
      case 'findText':
        IFace.WebToNative.showFindDialog(mailInfo.id)
        break
      default:
    }
  }

  onClickContainer = (e: React.MouseEvent): void => {
    // console.log(' --- onClickContainer : ', e);

    processEmailEvent(e)
  }

  getInfoElem = (info: InfoItem): React.ReactNode => {
    const { key, histories, value } = info

    let viewValue: string | null = null
    if (value != null) {
      viewValue = value
    } else {
      const historyList = histories!.sort((h1, h2) => {
        const date1 = h1.date || ''
        const date2 = h2.date || ''

        if (date1 < date2) {
          return 1
        } else if (date1 > date2) {
          return -1
        }
        return 0
      })

      console.log(' --- historyList : ', historyList)

      const lastHistory = historyList[0]
      viewValue = lastHistory.value
    }

    const valueElem = <div className={classNames(styles.col2)}>{viewValue}</div>

    return (
      <div className={classNames(styles.infoItem)} key={key}>
        <div className={classNames(styles.keyRow)}>
          <div className={classNames(styles.col1)}>
            <img src={exclamation24} alt="info" />
            <span className={classNames(styles.keyValue)}>
              <span className={classNames(styles.key)}>{key}</span>
              {valueElem}
            </span>
          </div>
        </div>
      </div>
    )
  }

  getFeedbackElems = (
    aiExtractionFeedback: boolean | null,
    aiExtractionRefresh: boolean,
  ): React.ReactNode => {
    return (
      <div className={classNames(styles.aiInfo)}>
        <span className={classNames(styles.aiDesc)}>{i18n.t('ai.desc.officemailAi')}</span>
        <span className={classNames(styles.aiFeedback)}>
          {uiExt.getButtonElem(
            this,
            'extraction-feedback-thumbUp',
            styles.thumbUp,
            aiExtractionFeedback === true ? thumbUpFilledSVG : thumbUpSVG,
            'extraction-feedback-thumbUp',
          )}
          {uiExt.getButtonElem(
            this,
            'extraction-feedback-thumbDown',
            styles.thumbDown,
            aiExtractionFeedback === false ? thumbDownFilledSVG : thumbDownSVG,
            'extraction-feedback-thumbDown',
          )}
          <Button
            className={classNames(styles.aiRefresh)}
            type="text"
            size="small"
            loading={aiExtractionRefresh}
            icon={<img src={refreshSVG} alt="refresh-ai-extraction" />}
            onClick={(e) => {
              this.onClickBtn('refresh-ai-extraction', e)
            }}
          />
        </span>
      </div>
    )
  }

  render(): React.ReactNode {
    const { aiExtraction, aiExtractionRefresh, threadImportant } = this.state

    const { threadItem, title, threadInfo } = this.props
    const { mailInfo } = threadItem

    const unfold = aiExtraction
    this.foldMap.aiExtraction = unfold

    let containerElem: React.ReactNode = null
    if (mailInfo?.has_ai_extraction && mailInfo?.ai_extraction != null) {
      containerElem = (
        <TextFindableNode
          className={classNames(styles.container, styles.subjectContainer, { hidden: !unfold })}
          onClick={this.onClickContainer}
          onFocusChange={({ focused }: { focused: boolean }) => {
            if (!focused) return

            this.setState({ aiExtraction: true })
            this.onaiExtractionStateChanged(true)
          }}
        >
          <AiExtraction mailId={mailInfo.id} aiExtraction={mailInfo.ai_extraction} />
          {this.getFeedbackElems(mailInfo.ai_extraction_feedback ?? null, aiExtractionRefresh)}
        </TextFindableNode>
      )
    }

    return (
      <React.Fragment>
        <EmailToolbar
          ref={this.props.forwardedRef}
          owner={this}
          title={title}
          threadInfo={threadInfo}
          hasAiExtraction={mailInfo?.has_ai_extraction}
          threadImportant={threadImportant}
        />
        <div className={classNames(styles.emailView)}>{containerElem}</div>
      </React.Fragment>
    )
  }
}

interface EmailToolbarProps {
  owner: EmailSubject
  title?: string
  threadInfo?: ThreadInfo | null
  hasAiExtraction?: boolean
  threadImportant: boolean | null
}

const EmailToolbar = forwardRef<any, EmailToolbarProps>(
  ({ owner, title, threadInfo, hasAiExtraction, threadImportant }, ref) => {
    const [openTextFinder, setOpenTextFinder] = useState<boolean>(false)
    const [portalElem, setPortalElem] = useState<Element | null>(null)
    const searchBarRef = useRef<any>(null)

    useEffect(() => {
      const subjectPortal = document.querySelector('.subjectPortal')
      if (subjectPortal != null) {
        setPortalElem(subjectPortal)
      }
    }, [])

    useEffect(() => {
      const cleanup = findTextObserver.register((text: string | null, options?: any) => {
        const hasText = text != null && text.length > 0
        if (hasText === true) {
          setOpenTextFinder(true)
        }
        options?.focus && setTimeout(() => searchBarRef.current?.focus(), 100)
      })
      return () => {
        cleanup()
      }
    }, [setOpenTextFinder])

    const subject =
      title === undefined ? (
        <LoadingOutlined spin />
      ) : (
        title || `(${i18n.t('email.desc.noSubject')})`
      )

    const FoldAiExtractionButton =
      hasAiExtraction &&
      uiExt.getFoldButtonElem(owner, 'aiExtraction', styles.aiExtrationBtn, null, 'ai extraction', {
        upIcon: <img src={chevronDown24} alt="unfold" style={{ width: '24px', height: '24px' }} />,
        downIcon: <img src={chevronUp24} alt="fold" style={{ width: '24px', height: '24px' }} />,
      })

    const FoldThreadButton = uiExt.getFoldButtonElem(
      owner,
      'foldThread',
      styles.foldThreadBtn,
      null,
      'fold thread',
      {
        upIcon: (
          <img
            src={toolbarCollapse24SVG}
            alt="collapse"
            style={{ width: '24px', height: '24px' }}
          />
        ),
        downIcon: (
          <img src={toolbarExpand24SVG} alt="expand" style={{ width: '24px', height: '24px' }} />
        ),
      },
    )

    const ImportantButton =
      threadImportant != null &&
      uiExt.getFoldButtonElem(
        owner,
        'threadImportant',
        styles.importantThreadBtn,
        null,
        'important',
        {
          upIcon: (
            <img src={important24SVG} alt="normal" style={{ width: '24px', height: '24px' }} />
          ),
          downIcon: (
            <img
              src={importantMark24SVG}
              alt="important"
              style={{ width: '24px', height: '24px' }}
            />
          ),
        },
      )

    const AiPreviewButton = hasAiExtraction && (
      <span>
        <img src={aiPreviewSVG} alt="organize" style={{ width: '24px', height: '24px' }} />
      </span>
    )

    const retElem = (
      <div ref={ref} className={styles.toolbar}>
        <div className={classNames(styles.subjectText)}>
          <TextFindableNode as="span" className={classNames(styles.subject)}>
            {subject}
          </TextFindableNode>
          {threadInfo?.labels?.map((label) => {
            const { id, name, text_color: color, background_color: bgColor } = label

            return (
              <div
                key={id}
                className={classNames(styles.threadLabel, { 'no-color': !color && !bgColor })}
                style={{ color, borderColor: bgColor, backgroundColor: bgColor }}
                onClick={() => IFace.WebToNative.onThreadLabelClick(id, name)}
              >
                {name}
              </div>
            )
          })}
        </div>

        <div className={classNames(styles.extra)}>
          {ImportantButton}
          {FoldThreadButton}
          <Button
            type="text"
            size="small"
            className={styles.findTextBtn}
            icon={<img src={search24SVG} alt="find-text" />}
            onClick={() => {
              const nextOpenTextFinder = !openTextFinder

              setOpenTextFinder(nextOpenTextFinder)
              if (nextOpenTextFinder) {
                setTimeout(() => searchBarRef.current?.focus(), 100)
              }
            }}
          />
          {AiPreviewButton}
          {FoldAiExtractionButton}
        </div>

        {openTextFinder && (
          <TextFinderSearchBar ref={searchBarRef} onClose={() => setOpenTextFinder(false)} />
        )}
      </div>
    )

    return portalElem ? ReactDOM.createPortal(retElem, portalElem) : retElem
  },
)

export default forwardRef<any, Omit<EmailSubjectProps, 'forwardedRef' | 'registerFindableNode'>>(
  (props, ref) => {
    const textFinderContext = useTextFinderContext()

    return (
      <EmailSubject
        {...props}
        forwardedRef={ref}
        registerFindableNode={textFinderContext.registerFindableNode}
      />
    )
  },
)
